import { RadioFormControlLabel, Typography } from "@kaltura/ds-react-components";
import { Container, StyledFormLabel } from "./CommonStyles";
import { Controller, useFormContext } from "react-hook-form";
import { styled } from "@mui/material";
import RadioGroupMui from "@mui/material/RadioGroup";
import FormLabelWithDescription from "./../form/form-label-with-description/FormLabelWithDescription";

export type RadioFieldOption = {
    label: string;
    value: string;
    description?: string;
};

export interface RadioGroupProps {
    label: string;
    id: string;
    required?: boolean;
    disabled?: boolean;
    options: RadioFieldOption[];
    inline?: boolean;
    variant?: "horizontal" | "vertical";
    description?: string;
    "aria-describedby"?: string;
}

export const RadioGroup = (props: RadioGroupProps) => {
    const {
        label,
        id,
        required,
        options,
        description,
        inline = false,
        variant = "vertical",
        "aria-describedby": ariaDescribedBy,
    } = props;
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const getRadioLabel = (option: RadioFieldOption) => {
        return option.description ? (
            <StyledLabel text={option.label} description={option.description} fontWeightBold={true} />
        ) : (
            option.label
        );
    };

    return (
        <Container inline={inline}>
            <StyledFormLabel htmlFor={id} required={required} text={label} fontWeightBold={true} id={`${id}-label`} />
            {description && <StyledDescription variant="body2">{description}</StyledDescription>}
            <div>
                <Controller
                    control={control}
                    name={id}
                    rules={{ required }}
                    render={({ field }) => (
                        <StyledRadioGroup
                            aria-describedby={ariaDescribedBy}
                            value={field.value ?? options?.[0]?.value}
                            onChange={(event) => field.onChange(event.target.value)}
                            name={`radio-buttons-group-${id}`}
                            id={id}
                            row={variant === "horizontal"}
                        >
                            {options?.map((item, index) => (
                                <RadioFormControlLabel key={index} label={getRadioLabel(item)} value={item.value} />
                            ))}
                        </StyledRadioGroup>
                    )}
                />
            </div>
        </Container>
    );
};

const StyledRadioGroup = styled(RadioGroupMui)<{ row?: boolean }>(({ theme, row }) => ({
    display: "flex",
    gap: theme.spacing(row ? 2 : 1),
}));

const StyledLabel = styled(FormLabelWithDescription)(({ theme }) => ({
    color: theme.kaltura.palette.tone4,
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    marginBottom: theme.spacing(1),
}));
